import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

export const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
    if (zoomLevel <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  useEffect(() => {
    resetZoom();
  }, [currentIndex]);

  if (currentIndex === null || !images[currentIndex]) return null;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 text-white hover:text-gray-300 transition-colors p-2 bg-black/50 rounded-full"
        aria-label="Close"
      >
        <X size={32} />
      </button>

      {/* Zoom Controls */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 flex gap-2 bg-black/50 rounded-full p-2">
        <button
          onClick={handleZoomOut}
          disabled={zoomLevel <= 1}
          className="text-white hover:text-gray-300 transition-colors p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Zoom out"
        >
          <ZoomOut size={24} />
        </button>
        <span className="text-white px-3 py-2 text-sm">{Math.round(zoomLevel * 100)}%</span>
        <button
          onClick={handleZoomIn}
          disabled={zoomLevel >= 3}
          className="text-white hover:text-gray-300 transition-colors p-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Zoom in"
        >
          <ZoomIn size={24} />
        </button>
      </div>

      {/* Left Arrow */}
      {images.length > 1 &&
      <button
        onClick={onPrev}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-50 text-white hover:text-gray-300 transition-all p-3 bg-black/50 rounded-full hover:bg-black/70"
        aria-label="Previous image"
      >
        <ChevronLeft size={40} />
      </button>
      }

      {/* Image */}
      <div 
        className="relative max-w-[90vw] max-h-[90vh] overflow-hidden cursor-move"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          src={images[currentIndex].image}
          alt={images[currentIndex].title}
          className="max-w-full max-h-[90vh] object-contain select-none transition-transform duration-200"
          style={{
            transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
            cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          draggable={false}
        />
      </div>

      {/* Right Arrow */}
      {images.length > 1 &&
      <button
        onClick={onNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-50 text-white hover:text-gray-300 transition-all p-3 bg-black/50 rounded-full hover:bg-black/70"
        aria-label="Next image"
      >
        <ChevronRight size={40} />
      </button>
      }

      {/* Image Counter */}
      {images.length > 1 &&
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-black/50 px-4 py-2 rounded-full">
        <span className="text-white text-sm">
          {currentIndex + 1} / {images.length}
        </span>
      </div>
      }

      {/* Image Title */}
      {images[currentIndex].title && (
        <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-50 bg-black/50 px-6 py-2 rounded-full">
          <span className="text-white text-sm font-medium">
            {images[currentIndex].title}
          </span>
        </div>
      )}
    </div>
  );
};
